/**
 * Recovery from stale-chunk 404s after a deploy (ui#1608).
 *
 * A deploy replaces the hashed chunk names a running tab still references, so its next lazy
 * import 404s and Vite surfaces it as a "Failed to fetch dynamically imported module" crash in
 * the router error boundary. Vite reports that exact failure as a cancelable `vite:preloadError`
 * event before throwing; reloading swaps in the fresh HTML whose imports all resolve again.
 *
 * The deploy keeps the previous builds' chunks in the bucket (deploy.yml syncs assets/ without
 * `--delete`), so this handler is the fallback for tabs older than the assets-retention window,
 * not the primary fix.
 *
 * Plain module, no React: installed once from the SSG entry's client-only callback.
 */

export const VITE_PRELOAD_ERROR_EVENT = "vite:preloadError";

/**
 * A second failure within this window means the reload did NOT heal the tab (the fresh HTML
 * itself references a missing chunk). Reloading again would loop forever, so the error is left
 * to propagate to the route error boundary instead.
 */
export const RELOAD_LOOP_WINDOW_MS = 60_000;

const RELOAD_STAMP_KEY = "tf-stale-chunk-reload";

type StampStorage = Pick<Storage, "getItem" | "setItem">;

export type StaleChunkRecoveryOptions = {
	reload: () => void;
	/**
	 * Accessor rather than a value: in some privacy modes the `sessionStorage` GETTER itself
	 * throws, and that must count as "storage unavailable", not crash the installer.
	 */
	storage: () => StampStorage;
	now?: () => number;
};

/**
 * True when no prior reload attempt is recorded inside the loop window. A malformed stamp counts
 * as absent — it can only mean foreign data under our key, never a recent attempt of ours.
 */
export function shouldAttemptReload(previousStamp: string | null, now: number): boolean {
	if (previousStamp === null) {
		return true;
	}
	const previous = Number(previousStamp);
	if (!Number.isFinite(previous)) {
		return true;
	}
	return now - previous > RELOAD_LOOP_WINDOW_MS;
}

export function installStaleChunkRecovery(target: EventTarget, options: StaleChunkRecoveryOptions): void {
	const now = options.now ?? Date.now;
	target.addEventListener(VITE_PRELOAD_ERROR_EVENT, (event) => {
		let stamped = false;
		try {
			const storage = options.storage();
			if (!shouldAttemptReload(storage.getItem(RELOAD_STAMP_KEY), now())) {
				return;
			}
			// Stamp BEFORE reloading — the stamp exists to be read by the post-reload page.
			storage.setItem(RELOAD_STAMP_KEY, String(now()));
			stamped = true;
		} catch {
			// Storage unavailable: without a surviving stamp the loop guard cannot work, and an
			// unguarded reload can loop forever. Let the error boundary handle it instead.
			return;
		}
		if (stamped) {
			// Cancels Vite's re-throw of the import error, so the boundary never flashes.
			event.preventDefault();
			options.reload();
		}
	});
}
